import { confirmedDate, daysBetween, localDate } from './daily';
import type { ClearType, SaveData, StoredEvent } from '../types';

const KEY = 'spot-difference-daily-mvp-v1';

const id = (): string => globalThis.crypto?.randomUUID?.() ?? `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export function freshSave(now = new Date()): SaveData {
  const date = localDate(now);
  return {
    schemaVersion: 1,
    installId: id(),
    installDate: date,
    lastConfirmedDate: date,
    tutorialDone: false,
    clears: {},
    attempts: {},
    failures: {},
    streakDays: 0,
    settings: { sound: true, vibration: true },
    events: [],
    sessionIndex: 0,
  };
}

function valid(candidate: unknown): candidate is SaveData {
  if (!candidate || typeof candidate !== 'object') return false;
  const value = candidate as Partial<SaveData>;
  return value.schemaVersion === 1
    && typeof value.installId === 'string'
    && typeof value.installDate === 'string'
    && typeof value.lastConfirmedDate === 'string'
    && typeof value.tutorialDone === 'boolean'
    && !!value.clears && typeof value.clears === 'object'
    && !!value.attempts && typeof value.attempts === 'object'
    && !!value.failures && typeof value.failures === 'object'
    && typeof value.streakDays === 'number' && Number.isFinite(value.streakDays)
    && !!value.settings && typeof value.settings === 'object'
    && typeof value.settings.sound === 'boolean'
    && typeof value.settings.vibration === 'boolean'
    && Array.isArray(value.events)
    && value.events.every((event) => !!event && typeof event === 'object'
      && typeof event.name === 'string'
      && typeof event.event_id === 'string'
      && typeof event.event_ts_utc === 'string')
    && typeof value.sessionIndex === 'number' && Number.isFinite(value.sessionIndex);
}

export function loadSave(storage: Storage = localStorage): { save: SaveData; recovered: boolean } {
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return { save: freshSave(), recovered: false };
    const candidate: unknown = JSON.parse(raw);
    if (!valid(candidate)) throw new Error('invalid_schema');
    candidate.lastConfirmedDate = confirmedDate(candidate);
    return { save: candidate, recovered: false };
  } catch {
    return { save: freshSave(), recovered: true };
  }
}

export function persist(save: SaveData, storage: Storage = localStorage): void {
  storage.setItem(KEY, JSON.stringify(save));
}

export function recordClear(save: SaveData, date: string, contentId: string, clearType: ClearType): { before: number; after: number; first: boolean } {
  const existing = save.clears[date];
  if (existing) return { before: save.streakDays, after: save.streakDays, first: false };
  const before = save.streakDays;
  save.streakDays = save.lastClearDate && daysBetween(save.lastClearDate, date) === 1 ? before + 1 : 1;
  save.lastClearDate = date;
  save.lastConfirmedDate = date > save.lastConfirmedDate ? date : save.lastConfirmedDate;
  save.clears[date] = { contentId, clearType, completedAt: new Date().toISOString() };
  return { before, after: save.streakDays, first: true };
}

export function appendEvent(save: SaveData, event: StoredEvent): void {
  if (save.events.some((item) => item.event_id === event.event_id)) return;
  save.events.push(event);
  if (save.events.length > 500) save.events.splice(0, save.events.length - 500);
}
